import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import CompanyDashboard from './pages/CompanyDashboard';
import GeneralManagerDashboard from './pages/GeneralManagerDashboard';
import OperationsManagementDashboard from './pages/OperationsManagementDashboard';
import ProjectTeamsDashboard from './pages/ProjectTeamsDashboard';
import AccountsDashboard from './pages/AccountsDashboard';
import FieldDashboard from './pages/FieldDashboard';
import { Toaster } from 'sonner';
import { ServiceWorkerUpdater } from './components/ServiceWorkerUpdater';
import { setupForegroundNotificationSound } from './lib/pushNotifications';
import { installAuthFetchInterceptor } from './lib/fetchInterceptor';
import { initAppVersionCheck } from './lib/appUpdate';
import { UpdateBanner } from './components/UpdateBanner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Install global 401-auto-logout interceptor BEFORE any API calls fire
    installAuthFetchInterceptor();

    // Kick off silent app-version check. If the server has a newer version
    // than what this tab is running, it will force-reload with fresh caches
    // before we render any stale data.
    initAppVersionCheck({ silent: true });

    const token = localStorage.getItem('token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
    // Setup foreground notification sound listener
    setupForegroundNotificationSound();
  }, []);

  // Re-check version every time a new user logs in (state transition null -> object).
  // Handles the case where the user kept a tab open overnight during a deploy.
  useEffect(() => {
    if (user) {
      initAppVersionCheck({ silent: true });
    }
  }, [user]);

  const fetchUser = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (loading) return <div className="loading-screen">Loading...</div>;
    if (!user) return <Navigate to="/auth" />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" />;
    }
    return children;
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <UpdateBanner />
        <ServiceWorkerUpdater />
        <Routes>
          <Route path="/" element={user ? <DashboardRouter user={user} /> : <LandingPage />} />
          <Route path="/auth" element={!user ? <AuthPage setUser={setUser} /> : <Navigate to="/" />} />
          
          {/* Platform Dashboard - Super Admin */}
          <Route
            path="/dashboard/platform"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* Company Dashboard - Company Admin only */}
          <Route
            path="/dashboard/company"
            element={
              <ProtectedRoute allowedRoles={['company_admin']}>
                <CompanyDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* General Manager / Director Dashboard */}
          <Route
            path="/dashboard/management"
            element={
              <ProtectedRoute allowedRoles={['director', 'general_manager']}>
                <GeneralManagerDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* Operations Management Dashboard - For other managers */}
          <Route
            path="/dashboard/operations"
            element={
              <ProtectedRoute allowedRoles={['senior_manager', 'manager', 'project_manager']}>
                <OperationsManagementDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* Project Teams Dashboard */}
          <Route
            path="/dashboard/project-teams"
            element={
              <ProtectedRoute allowedRoles={['estimator', 'project_coordinator']}>
                <ProjectTeamsDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* Accounts Dashboard */}
          <Route
            path="/dashboard/accounts"
            element={
              <ProtectedRoute allowedRoles={['accounts', 'purchaser', 'accounts_purchaser']}>
                <AccountsDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          
          {/* Field Dashboard - Field Workers ONLY */}
          <Route
            path="/dashboard/field"
            element={
              <ProtectedRoute allowedRoles={['employee', 'subcontractor', 'team_lead']}>
                <FieldDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function DashboardRouter({ user }) {
  // Platform level
  if (user.role === 'super_admin') return <Navigate to="/dashboard/platform" />;
  
  // Company owner
  if (user.role === 'company_admin') return <Navigate to="/dashboard/company" />;
  
  // General Manager / Director - Full management dashboard
  if (['director', 'general_manager'].includes(user.role)) {
    return <Navigate to="/dashboard/management" />;
  }
  
  // Operations Management - Other managers
  if (['senior_manager', 'manager', 'project_manager'].includes(user.role)) {
    return <Navigate to="/dashboard/operations" />;
  }
  
  // Project Teams (Estimator, Project Coordinator)
  if (['estimator', 'project_coordinator'].includes(user.role)) {
    return <Navigate to="/dashboard/project-teams" />;
  }
  
  // Accounts & Payroll
  if (['accounts', 'purchaser', 'accounts_purchaser'].includes(user.role)) {
    return <Navigate to="/dashboard/accounts" />;
  }
  
  // Field Workers
  if (['employee', 'subcontractor', 'team_lead'].includes(user.role)) {
    return <Navigate to="/dashboard/field" />;
  }
  
  return <Navigate to="/auth" />;
}

export default App;
